import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Save, User, Building2, Stethoscope, Home, Dog, Cat, Camera, MapPin, CreditCard, Shield, FileText, PawPrint } from "lucide-react";
import { FormSection, CheckboxCard, RadioOption, FormInput, FileUpload } from "@/components/FormElements";
import { toast } from "sonner";
import { ModalCorteImagem } from '@/components/ModalCorteImagem';

type TipoPrestador = "pf" | "pj" | "mv";

const especialidadesMV = [
  "Acupuntura Veterinária", "Anestesiologia Veterinária", "Cardiologia Veterinária",
  "Cirurgia Veterinária", "Clínica Médica de Pequenos Animais", "Dermatologia Veterinária",
  "Diagnóstico por Imagem", "Endocrinologia Veterinária", "Homeopatia Veterinária",
  "Medicina de Animais Selvagens", "Medicina Felina", "Medicina Veterinária Intensiva",
  "Medicina Veterinária do Coletivo", "Nefrologia e Urologia Veterinárias",
  "Nutrição e Nutrologia de Cães e Gatos", "Oftalmologia Veterinária",
  "Oncologia Veterinária", "Patologia Veterinária",
];

type AnfitriaoPerfilEditarProps = {
  embedded?: boolean;
};

type RegraOperacionalKey = "hospedagem" | "creche";

type RegrasOperacionaisState = Record<
  RegraOperacionalKey,
  {
    checkin_padrao: string;
    checkout_padrao: string;
    tolerancia_checkout_minutos: string;
    late_checkout_limite: string;
    late_checkout_tipo: string;
    taxa_atraso: string;
    cobrar_atraso_checkout: string;
  }
>;

const normalizarValorMonetario = (valor: string): string => {
  const valorLimpo = valor.trim();

  if (!valorLimpo) {
    return "";
  }

  const somenteDigitos = valorLimpo.replace(/\D/g, "");
  if (somenteDigitos) {
    return (Number(somenteDigitos) / 100).toFixed(2);
  }

  const numero = Number(valorLimpo.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(numero) ? numero.toFixed(2) : "";
};

const formatarValorMonetario = (valor: string): string => {
  const normalizado = normalizarValorMonetario(valor);
  if (!normalizado) {
    return "";
  }

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(normalizado));
};

const EditarPerfil = ({
  embedded = false,
}: AnfitriaoPerfilEditarProps) => {

  const [tipo, setTipo] = useState<TipoPrestador>("pf");
  const [servicos, setServicos] = useState<number[]>([]);
  const [maxPets, setMaxPets] = useState({
    hospedagem: "3",
    creche: "5",
    passeio: "3",
    petsitter: "1",
    taxi_dog: "2",
    adestramento: "1",
    banho_tosa: "1",
  });
  const [maxAtendimentosDia, setMaxAtendimentosDia] = useState({
    passeio: "3",
    petsitter: "3",
    taxi_dog: "4",
    adestramento: "2",
    vet_casa: "4",
    banho_tosa: "8",
  });
  const [documentosAtuais, setDocumentosAtuais] = useState({
    documento_frente: "",
    documento_verso: "",
    selfie_documento: "",
    comprovante_endereco: "",
  });
  const [regrasOperacionais, setRegrasOperacionais] = useState<RegrasOperacionaisState>({
    hospedagem: {
      checkin_padrao: "12:00",
      checkout_padrao: "12:00",
      tolerancia_checkout_minutos: "0",
      late_checkout_limite: "18:00",
      late_checkout_tipo: "creche_ate_limite_diaria_apos",
      taxa_atraso: "",
      cobrar_atraso_checkout: "1",
    },
    creche: {
      checkin_padrao: "07:00",
      checkout_padrao: "19:00",
      tolerancia_checkout_minutos: "30",
      late_checkout_limite: "21:00",
      late_checkout_tipo: "nova_creche",
      taxa_atraso: "",
      cobrar_atraso_checkout: "1",
    },
  });
  const [bio, setBio] = useState("");
  const [fotosAtuais, setFotosAtuais] = useState({
    perfil: "",
    espaco: ["", "", "", ""]
  });
  const [preferencias, setPreferencias] = useState({
    aceita_caes: true, aceita_gatos: true, aceita_outros: false,
    aceita_ate_10kg: true, aceita_ate_25kg: true, aceita_ate_40kg: false, aceita_acima_40kg: false,
    aceita_idosos: false, aceita_filhotes: true, administra_medicacao: false,
  });
  const [endereco, setEndereco] = useState({
    cep: "", endereco: "", numero: "", bairro: "", cidade: "", estado: "", complemento: "",
  });
  const [chavePix, setChavePix] = useState("");
  const [politica, setPolitica] = useState("moderada");
  const [mvEspecialidades, setMvEspecialidades] = useState<string[]>([]);
  const [mvModalidades, setMvModalidades] = useState({ domiciliar: false, teleconsulta: false, consultorio: false });
  const [rtData, setRtData] = useState({ nome: "", crmv: "", telefone: "", email: "" });
  const [mvData, setMvData] = useState({ crmv: "", telefone: "", email: "" });
  const [termosAceitos, setTermosAceitos] = useState({ faq: false, video: false, termos: false });
  const [modalCorteProps, setModalCorteProps] = useState({
    isOpen: false,
    imagemSrc: "",
    targetIndex: -1, // -1 para perfil, 0 a 3 para fotos do espaço
  });

  const [mvArquivos, setMvArquivos] = useState({
    doc_foto: null as File | null,
    doc_crmv: null as File | null,
    doc_endereco_prof: null as File | null,
  });

  const [mvDocumentosAtuais, setMvDocumentosAtuais] = useState({
    doc_foto: "",
    doc_crmv: "",
    doc_endereco_prof: "",
  });
  const [servicoEmFoco, setServicoEmFoco] = useState<number | null>(null);
  const secaoServicosRef = useRef<HTMLDivElement | null>(null);

  const [mvConformidades, setMvConformidades] = useState<string[]>([]);
  const toggleConformidade = (item: string) => {
    setMvConformidades((prev) =>
      prev.includes(item)
        ? prev.filter((i) => i !== item)
        : [...prev, item]
    );
  };

  useEffect(() => {
    const carregarPerfil = async () => {
      try {
        const resp = await fetch("/anfitriao/perfil-dados", {
          headers: {
            "Accept": "application/json",
          },
          credentials: "include",
        });

        if (!resp.ok) {
          throw new Error("Falha ao carregar perfil");
        }

        const data = await resp.json();

        const storageUrl = (path?: string) => {
          if (!path) return "";
          if (path.startsWith("http")) return path;
          if (path.startsWith("/storage/")) return path;
          return `/storage/${path}`;
        };

        setDocumentosAtuais({
          documento_frente: storageUrl(data.documentos?.documento_frente),
          documento_verso: storageUrl(data.documentos?.documento_verso),
          selfie_documento: storageUrl(data.documentos?.selfie_documento),
          comprovante_endereco: storageUrl(data.documentos?.comprovante_endereco),
        });

        console.log("DADOS DA API:", data);

        // 1. Mapear o Tipo de Prestador corretamente
        const tipoMap: Record<string, TipoPrestador> = {
          pessoa_fisica: "pf",
          pessoa_juridica: "pj",
          medico_veterinario: "mv"
        };
        const tipoVindoDaApi = data.host_request?.tipo_prestador || "pessoa_fisica";
        setTipo(tipoMap[tipoVindoDaApi] || "pf");

        // 2. Mapear Serviços
        const novosServicos: number[] = [];
        if (data.host_request?.hospedagem) novosServicos.push(1);
        if (data.host_request?.creche) novosServicos.push(2);
        if (data.host_request?.passeio) novosServicos.push(3);
        if (data.host_request?.petsitter) novosServicos.push(4);
        if (data.host_request?.taxi_dog) novosServicos.push(5);
        if (data.host_request?.adestramento) novosServicos.push(6);
        if (data.host_request?.vet_casa) novosServicos.push(7);
        if (data.host_request?.banho_tosa) novosServicos.push(8);
        setServicos(novosServicos);

        // 3. Capacidade dos serviços
        setMaxPets({
          hospedagem: data.host_request?.max_pets_hospedagem?.toString() || "3",
          creche: data.host_request?.max_pets_creche?.toString() || "5",
          passeio: data.host_request?.max_pets_passeio?.toString() || "3",
          petsitter: data.host_request?.max_pets_petsitter?.toString() || "1",
          taxi_dog: data.host_request?.max_pets_taxi_dog?.toString() || "2",
          adestramento: data.host_request?.max_pets_adestramento?.toString() || "1",
          banho_tosa: data.host_request?.max_pets_banho_tosa?.toString() || "1",
        });

        setMaxAtendimentosDia({
          passeio: data.host_request?.max_atendimentos_passeio?.toString() || "3",
          petsitter: data.host_request?.max_atendimentos_petsitter?.toString() || "3",
          taxi_dog: data.host_request?.max_atendimentos_taxi_dog?.toString() || "4",
          adestramento: data.host_request?.max_atendimentos_adestramento?.toString() || "2",
          vet_casa: data.host_request?.max_atendimentos_vet_casa?.toString() || "4",
          banho_tosa: data.host_request?.max_atendimentos_banho_tosa?.toString() || "8",
        });

        try {
          const capacidadeResp = await fetch("/anfitriao/servicos/limites", {
            headers: { Accept: "application/json" },
            credentials: "include",
          });

          if (capacidadeResp.ok) {
            const capacidadeData = await capacidadeResp.json();
            const capacidadeItems = Array.isArray(capacidadeData.items) ? capacidadeData.items : [];

            const petsPorServico: Partial<typeof maxPets> = {};
            const atendimentosPorServico: Partial<typeof maxAtendimentosDia> = {};

            capacidadeItems.forEach((item: any) => {
              const tipoServicoId = Number(item.tipo_servico_id);

              if (item.max_pets_simultaneos !== undefined) {
                if (tipoServicoId === 1) petsPorServico.hospedagem = String(item.max_pets_simultaneos || 3);
                if (tipoServicoId === 2) petsPorServico.creche = String(item.max_pets_simultaneos || 5);
                if (tipoServicoId === 3) petsPorServico.passeio = String(item.max_pets_simultaneos || 3);
                if (tipoServicoId === 4) petsPorServico.petsitter = String(item.max_pets_simultaneos || 1);
                if (tipoServicoId === 5) petsPorServico.taxi_dog = String(item.max_pets_simultaneos || 2);
                if (tipoServicoId === 6) petsPorServico.adestramento = String(item.max_pets_simultaneos || 1);
                if (tipoServicoId === 8) petsPorServico.banho_tosa = String(item.max_pets_simultaneos || 1);
              }

              if (item.usa_limite_diario !== false && item.max_reservas_dia !== undefined) {
                if (tipoServicoId === 3) atendimentosPorServico.passeio = String(item.max_reservas_dia || 3);
                if (tipoServicoId === 4) atendimentosPorServico.petsitter = String(item.max_reservas_dia || 3);
                if (tipoServicoId === 5) atendimentosPorServico.taxi_dog = String(item.max_reservas_dia || 4);
                if (tipoServicoId === 6) atendimentosPorServico.adestramento = String(item.max_reservas_dia || 2);
                if (tipoServicoId === 7) atendimentosPorServico.vet_casa = String(item.max_reservas_dia || 4);
                if (tipoServicoId === 8) atendimentosPorServico.banho_tosa = String(item.max_reservas_dia || 8);
              }
            });

            setMaxPets((prev) => ({ ...prev, ...petsPorServico }));
            setMaxAtendimentosDia((prev) => ({ ...prev, ...atendimentosPorServico }));
          }
        } catch (error) {
          console.warn("Não foi possível carregar a capacidade dos serviços.", error);
        }

        setRegrasOperacionais({
          hospedagem: {
            checkin_padrao: data.servicos_config?.hospedagem?.checkin_padrao || "12:00",
            checkout_padrao: data.servicos_config?.hospedagem?.checkout_padrao || "12:00",
            tolerancia_checkout_minutos: String(data.servicos_config?.hospedagem?.tolerancia_checkout_minutos ?? 0),
            late_checkout_limite: data.servicos_config?.hospedagem?.late_checkout_limite || "18:00",
            late_checkout_tipo: data.servicos_config?.hospedagem?.late_checkout_tipo || "creche_ate_limite_diaria_apos",
            taxa_atraso: data.servicos_config?.hospedagem?.taxa_atraso !== null && data.servicos_config?.hospedagem?.taxa_atraso !== undefined
              ? normalizarValorMonetario(String(data.servicos_config?.hospedagem?.taxa_atraso))
              : "",
            cobrar_atraso_checkout: data.servicos_config?.hospedagem?.cobrar_atraso_checkout ? "1" : "0",
          },
          creche: {
            checkin_padrao: data.servicos_config?.creche?.checkin_padrao || "07:00",
            checkout_padrao: data.servicos_config?.creche?.checkout_padrao || "19:00",
            tolerancia_checkout_minutos: String(data.servicos_config?.creche?.tolerancia_checkout_minutos ?? 30),
            late_checkout_limite: data.servicos_config?.creche?.late_checkout_limite || "21:00",
            late_checkout_tipo: data.servicos_config?.creche?.late_checkout_tipo || "nova_creche",
            taxa_atraso: data.servicos_config?.creche?.taxa_atraso !== null && data.servicos_config?.creche?.taxa_atraso !== undefined
              ? normalizarValorMonetario(String(data.servicos_config?.creche?.taxa_atraso))
              : "",
            cobrar_atraso_checkout: data.servicos_config?.creche?.cobrar_atraso_checkout ? "1" : "0",
          },
        });

        // 4. Bio
        setBio(data.host_profile?.bio || "");

        // 5. Fotos
        setFotosAtuais({
          perfil: data.host_profile?.foto_perfil || "",
          espaco: data.host_profile?.fotos_espaco || ["", "", "", ""],
        });

        // 5.1 Preferências
        setPreferencias({
          aceita_caes: data.preferencias?.aceita_caes ?? true,
          aceita_gatos: data.preferencias?.aceita_gatos ?? true,
          aceita_outros: data.preferencias?.aceita_outros ?? false,
          aceita_ate_10kg: data.preferencias?.aceita_ate_10kg ?? true,
          aceita_ate_25kg: data.preferencias?.aceita_ate_25kg ?? true,
          aceita_ate_40kg: data.preferencias?.aceita_ate_40kg ?? false,
          aceita_acima_40kg: data.preferencias?.aceita_acima_40kg ?? false,
          aceita_idosos: data.preferencias?.aceita_idosos ?? false,
          aceita_filhotes: data.preferencias?.aceita_filhotes ?? true,
          administra_medicacao: data.preferencias?.administra_medicacao ?? false,
        });

        // 6. Endereço (Pegando do host_profile conforme sua API)
        setEndereco({
          cep: data.host_profile?.cep || "",
          endereco: data.host_profile?.rua || "", // A API chama de 'rua'
          numero: data.host_profile?.numero || "",
          bairro: data.host_profile?.bairro || "",
          cidade: data.host_profile?.cidade || "",
          estado: data.host_profile?.estado || "",
          complemento: data.host_profile?.complemento || "",
        });

        // 7. PIX
        setChavePix(data.host_profile?.pix_chave || "");

        // 8. Confirmações (Mapeando do objeto 'confirmacoes' da API)
        setTermosAceitos({
          faq: data.confirmacoes?.aceita_politicas || false,
          video: data.confirmacoes?.confirma_veracidade || false,
          termos: data.confirmacoes?.aceita_termos || false,
        });

        // 9. Dados do Médico Veterinário
        if (data.mv) {
          setMvData({
            crmv: data.mv.dados?.crmv || "",
            telefone: data.mv.dados?.telefone || "",
            email: data.mv.dados?.email || "",
          });
          
          setMvModalidades({
            domiciliar: data.mv.modalidades?.domiciliar || false,
            teleconsulta: data.mv.modalidades?.teleconsulta || false,
            consultorio: data.mv.modalidades?.consultorio || false,
          });
          
          setMvDocumentosAtuais({
            doc_foto: data.mv?.documentos?.doc_foto || data.mv?.doc_foto || data.documentos?.doc_foto || "",
            doc_crmv: data.mv?.documentos?.doc_crmv || data.mv?.doc_crmv || data.documentos?.doc_crmv || "",
            doc_endereco_prof: data.mv?.documentos?.doc_endereco_prof || data.mv?.doc_endereco_prof || data.documentos?.doc_endereco_prof || "",
          });

          setMvEspecialidades(Array.isArray(data.mv.especialidades) ? data.mv.especialidades : []);
          setMvConformidades(Array.isArray(data.mv.conformidades) ? data.mv.conformidades : []);
        
        }

      } catch (error: any) {
        toast.error(error.message || "Erro ao carregar perfil");
      }
    };

    carregarPerfil();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const secao = params.get("secao");
    const servicoId = Number(params.get("servico_id"));

    if (Number.isInteger(servicoId) && servicoId > 0) {
      setServicoEmFoco(servicoId);
    }

    if ((secao === "servicos" || servicoId > 0) && secaoServicosRef.current) {
      window.setTimeout(() => {
        secaoServicosRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
  }, []);

  const toggleServico = (id: number) => {
    setServicos((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  const togglePref = (key: keyof typeof preferencias) => {
    setPreferencias((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const atualizarRegraOperacional = (
    tipoRegra: RegraOperacionalKey,
    campo: keyof RegrasOperacionaisState[RegraOperacionalKey],
    valor: string
  ) => {
    setRegrasOperacionais((prev) => ({
      ...prev,
      [tipoRegra]: {
        ...prev[tipoRegra],
        [campo]: valor,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const formData = new FormData();

      formData.append("tipo", tipo);
      formData.append("bio", bio);
      formData.append("chavePix", chavePix);
      formData.append("politica", politica);

      Object.entries(endereco).forEach(([key, value]) => {
        formData.append(`endereco[${key}]`, value || "");
      });

      Object.entries(preferencias).forEach(([key, value]) => {
        formData.append(`preferencias[${key}]`, value ? "1" : "0");
      });

      Object.entries(maxPets).forEach(([key, value]) => {
        formData.append(`maxPets[${key}]`, value || "");
      });

      Object.entries(maxAtendimentosDia).forEach(([key, value]) => {
        formData.append(`maxAtendimentosDia[${key}]`, value || "");
      });

      (Object.keys(regrasOperacionais) as RegraOperacionalKey[]).forEach((grupo) => {
        Object.entries(regrasOperacionais[grupo]).forEach(([key, value]) => {
          formData.append(`regrasOperacionais[${grupo}][${key}]`, value || "");
        });
      });

      servicos.forEach((servicoId, index) => {
        formData.append(`servicos[${index}]`, String(servicoId));
      });

      Object.entries(termosAceitos).forEach(([key, value]) => {
        formData.append(`termosAceitos[${key}]`, value ? "1" : "0");
      });

      Object.entries(mvData).forEach(([key, value]) => {
        formData.append(`mvData[${key}]`, value || "");
      });

      Object.entries(mvModalidades).forEach(([key, value]) => {
        formData.append(`mvModalidades[${key}]`, value ? "1" : "0");
      });

      mvEspecialidades.forEach((item, index) => {
        formData.append(`mvEspecialidades[${index}]`, item);
      });

      mvConformidades.forEach((item, index) => {
        formData.append(`mvConformidades[${index}]`, item);
      });

      if (mvArquivos.doc_foto) {
        formData.append("mv_doc_foto", mvArquivos.doc_foto);
      }

      if (mvArquivos.doc_crmv) {
        formData.append("mv_doc_crmv", mvArquivos.doc_crmv);
      }

      if (mvArquivos.doc_endereco_prof) {
        formData.append("mv_doc_endereco_prof", mvArquivos.doc_endereco_prof);
      }

      const documentoFrente = document.querySelector<HTMLInputElement>('input[name="documento_frente"]');
      const documentoVerso = document.querySelector<HTMLInputElement>('input[name="documento_verso"]');
      const selfieDocumento = document.querySelector<HTMLInputElement>('input[name="selfie_documento"]');
      const comprovanteEndereco = document.querySelector<HTMLInputElement>('input[name="comprovante_endereco"]');

      if (documentoFrente?.files?.[0]) {
        formData.append("documento_frente", documentoFrente.files[0]);
      }

      if (documentoVerso?.files?.[0]) {
        formData.append("documento_verso", documentoVerso.files[0]);
      }

      if (selfieDocumento?.files?.[0]) {
        formData.append("selfie_documento", selfieDocumento.files[0]);
      }

      if (comprovanteEndereco?.files?.[0]) {
        formData.append("comprovante_endereco", comprovanteEndereco.files[0]);
      }

      if (fotosAtuais.perfil) {
        formData.append("foto_perfil_base64", fotosAtuais.perfil);
      }

      fotosAtuais.espaco.forEach((foto, index) => {
        if (foto) {
          formData.append(`fotos_espaco_base64[${index}]`, foto);
        }
      });

      const resp = await fetch("/anfitriao/perfil-dados", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-TOKEN":
            (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || "",
        },
        credentials: "include",
        body: formData,
      });

      const data = await resp.json();

      if (!resp.ok || !data.ok) {
        throw new Error(data.message || "Falha ao salvar perfil");
      }

      const token = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || "";
      const capacidadeResp = await fetch("/anfitriao/servicos/limites", {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": token,
        },
        body: JSON.stringify({
          limitesPorTipo: {
            3: maxAtendimentosDia.passeio,
            4: maxAtendimentosDia.petsitter,
            5: maxAtendimentosDia.taxi_dog,
            6: maxAtendimentosDia.adestramento,
            7: maxAtendimentosDia.vet_casa,
            8: maxAtendimentosDia.banho_tosa,
          },
          petsPorTipo: {
            1: maxPets.hospedagem,
            2: maxPets.creche,
            3: maxPets.passeio,
            4: maxPets.petsitter,
            5: maxPets.taxi_dog,
            6: maxPets.adestramento,
            8: maxPets.banho_tosa,
          },
        }),
      });

      const capacidadeData = await capacidadeResp.json().catch(() => ({}));
      if (!capacidadeResp.ok || capacidadeData.ok === false) {
        throw new Error(capacidadeData.message || "Perfil salvo, mas houve erro ao salvar a capacidade dos serviços.");
      }

      toast.success("Perfil salvo com sucesso!");
    } catch (error: any) {
      toast.error(error.message || "Erro ao salvar perfil");
    }
  };

  const handleSelecionarFoto = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Converte o arquivo para uma URL temporária para jogar no Modal
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setModalCorteProps({
          isOpen: true,
          imagemSrc: reader.result?.toString() || "",
          targetIndex: index,
        });
      });
      reader.readAsDataURL(file);
      
      // Reseta o input para permitir selecionar a mesma foto novamente se errar
      e.target.value = ''; 
    }
  };

  const servicosList = [
    { id: 1, label: "🏠 Hospedagem", qtdKey: "hospedagem" as const },
    { id: 2, label: "🏫 Creche para pets", qtdKey: "creche" as const },
    { id: 3, label: "🐕 Passeio com cães", qtdKey: "passeio" as const, atendimentoKey: "passeio" as const },
    { id: 4, label: "🏡 Pet Sitter", qtdKey: "petsitter" as const, atendimentoKey: "petsitter" as const },
    { id: 5, label: "🚗 Táxi pet", qtdKey: "taxi_dog" as const, atendimentoKey: "taxi_dog" as const },
    { id: 6, label: "🎓 Adestramento", qtdKey: "adestramento" as const, atendimentoKey: "adestramento" as const },
    { id: 8, label: "🚿 Banho e Tosa", qtdKey: "banho_tosa" as const, atendimentoKey: "banho_tosa" as const },
  ];

  const showServico7 = tipo === "pj" || tipo === "mv";
  const exibirRegrasOperacionais = servicos.includes(1) || servicos.includes(2);

  return (
    <div className={embedded ? "w-full" : "min-h-screen gradient-hero"}>
  {embedded ? (
    <div className="mb-6 overflow-hidden rounded-[28px] bg-[#2f945c] shadow-sm">
      <div className="flex flex-col gap-4 px-6 py-7 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-white">
            Editar Perfil
          </h1>
          <p className="mt-2 text-sm text-white/90">
            Gerencie seus dados, documentos, serviços e preferências em um só lugar.
          </p>
        </div>

        <div className="w-fit rounded-2xl bg-white/15 px-5 py-3 text-white">
          <p className="text-sm font-semibold">Modo atual</p>
          <p className="mt-1 text-[15px] font-medium">
            {tipo === "pf"
              ? "Pessoa Física"
              : tipo === "pj"
              ? "Pessoa Jurídica"
              : "Médico Veterinário"}
          </p>
        </div>
      </div>
    </div>
  ) : (
    <header className="gradient-primary px-4 py-6">
      <div className="mx-auto max-w-3xl">
        <nav className="mb-3 text-sm text-primary-foreground/70">
          <span>Início</span> <span className="mx-2">›</span>
          <span>Minha Conta</span> <span className="mx-2">›</span>
          <span className="text-primary-foreground font-semibold">Editar Perfil</span>
        </nav>
        <h1 className="flex items-center gap-3 text-2xl font-extrabold text-primary-foreground">
          <PawPrint className="h-7 w-7" />
          Editar Perfil do Prestador
        </h1>
      </div>
    </header>
  )}

  <main className={embedded ? "mx-auto w-full max-w-4xl px-0 py-0" : "mx-auto max-w-3xl px-4 py-8"}>
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* 1. Tipo de Prestador */}
          <FormSection number={1} title="Como você quer prestar seus serviços?">
            <div className="grid gap-3 sm:grid-cols-3">
              <RadioOption name="tipo" value="pf" label="Pessoa Física" description="Anfitrião autônomo" icon="👤" checked={tipo === "pf"} onChange={(v) => setTipo(v as TipoPrestador)} />
              <RadioOption name="tipo" value="pj" label="Pessoa Jurídica" description="Empresa / CNPJ" icon="🏢" checked={tipo === "pj"} onChange={(v) => setTipo(v as TipoPrestador)} />
              <RadioOption name="tipo" value="mv" label="Médico Veterinário" description="CRMV ativo" icon="🩺" checked={tipo === "mv"} onChange={(v) => setTipo(v as TipoPrestador)} />
            </div>
          </FormSection>

          {/* 2. Serviços */}
          <div ref={secaoServicosRef}>
          <FormSection number={2} title="Serviços que deseja oferecer">
            {servicoEmFoco && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                Serviço selecionado para edição. Revise abaixo as opções e salve ao final.
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {servicosList.map((s) => (
                <CheckboxCard
                  key={s.id}
                  id={`serv${s.id}`}
                  label={s.label}
                  checked={servicos.includes(s.id)}
                  onChange={() => toggleServico(s.id)}
                  className={servicoEmFoco === s.id ? "rounded-xl ring-2 ring-emerald-300 ring-offset-2" : ""}
                >
                  {servicos.includes(s.id) && (s.qtdKey || s.atendimentoKey) && (
                    <div className="mt-3 space-y-2 rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                      {s.qtdKey && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">Quantos pets simultâneos no atendimento?</span>
                          <input
                            type="number"
                            min="1"
                            max="20"
                            value={maxPets[s.qtdKey]}
                            onChange={(e) => setMaxPets((p) => ({ ...p, [s.qtdKey!]: e.target.value }))}
                            className="w-16 rounded-md border border-input bg-background px-2 py-1 text-center text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20"
                          />
                        </div>
                      )}

                      {s.atendimentoKey && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">Máximo de atendimentos por dia?</span>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={maxAtendimentosDia[s.atendimentoKey]}
                            onChange={(e) => setMaxAtendimentosDia((p) => ({ ...p, [s.atendimentoKey!]: e.target.value }))}
                            className="w-16 rounded-md border border-input bg-background px-2 py-1 text-center text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </CheckboxCard>
              ))}

              {showServico7 && (
                <CheckboxCard
                  id="serv7"
                  label="🩺 Atendimento veterinário em domicílio"
                  checked={servicos.includes(7)}
                  onChange={() => toggleServico(7)}
                  className={servicoEmFoco === 7 ? "rounded-xl ring-2 ring-emerald-300 ring-offset-2" : ""}
                >
                  {servicos.includes(7) && (
                    <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">Máximo de atendimentos por dia?</span>
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={maxAtendimentosDia.vet_casa}
                          onChange={(e) => setMaxAtendimentosDia((p) => ({ ...p, vet_casa: e.target.value }))}
                          className="w-16 rounded-md border border-input bg-background px-2 py-1 text-center text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20"
                        />
                      </div>
                    </div>
                  )}
                </CheckboxCard>
              )}
            </div>

            {tipo === "pj" && servicos.includes(7) && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-lg border-2 border-accent bg-muted p-4">
                <p className="mb-3 text-sm font-semibold text-coral">⚠️ Para oferecer atendimento veterinário, sua empresa precisa ter um Médico Veterinário como Responsável Técnico.</p>
                <h4 className="mb-3 text-sm font-bold text-foreground">Dados do Responsável Técnico (RT)</h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormInput label="Nome completo" name="rt_nome" value={rtData.nome} onChange={(v) => setRtData((p) => ({ ...p, nome: v }))} required />
                  <FormInput label="CRMV-UF" name="rt_crmv" value={rtData.crmv} onChange={(v) => setRtData((p) => ({ ...p, crmv: v }))} required placeholder="Ex: CRMV-SP 12345" />
                  <FormInput label="Telefone" name="rt_telefone" type="tel" value={rtData.telefone} onChange={(v) => setRtData((p) => ({ ...p, telefone: v }))} required />
                  <FormInput label="E-mail profissional" name="rt_email" type="email" value={rtData.email} onChange={(v) => setRtData((p) => ({ ...p, email: v }))} required />
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <FileUpload label="Documento com foto (RT)" name="rt_doc_foto" />
                  <FileUpload label="Comprovante CRMV (RT)" name="rt_doc_crmv" />
                  <FileUpload label="Comprovante endereço (RT)" name="rt_doc_endereco" />
                </div>
              </motion.div>
            )}
          </FormSection>
          </div>

          {/* 3. Regras operacionais */}
          {exibirRegrasOperacionais && (
            <FormSection number={3} title="Check-in, checkout e regras de atraso">
              <div className="space-y-4">
                {servicos.includes(1) && (
                  <div className="rounded-2xl border border-border bg-card p-4">
                    <div className="mb-4">
                      <h3 className="text-base font-bold text-foreground">Hospedagem</h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Defina os horários padrão e a regra de late checkout para hospedagem.
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <div className="sm:col-span-2 lg:col-span-3">
                        <label className="mb-2 block text-sm font-semibold text-foreground">
                          Cobrar atraso no checkout?
                        </label>
                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                            <input
                              type="radio"
                              name="hospedagem_cobrar_atraso_checkout"
                              checked={regrasOperacionais.hospedagem.cobrar_atraso_checkout === "1"}
                              onChange={() => atualizarRegraOperacional("hospedagem", "cobrar_atraso_checkout", "1")}
                              className="h-4 w-4 accent-primary"
                            />
                            Sim
                          </label>
                          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                            <input
                              type="radio"
                              name="hospedagem_cobrar_atraso_checkout"
                              checked={regrasOperacionais.hospedagem.cobrar_atraso_checkout === "0"}
                              onChange={() => atualizarRegraOperacional("hospedagem", "cobrar_atraso_checkout", "0")}
                              className="h-4 w-4 accent-primary"
                            />
                            Não
                          </label>
                        </div>
                      </div>

                      <FormInput
                        label="Check-in padrão"
                        name="hospedagem_checkin_padrao"
                        type="time"
                        value={regrasOperacionais.hospedagem.checkin_padrao}
                        onChange={(v) => atualizarRegraOperacional("hospedagem", "checkin_padrao", v)}
                      />
                      <FormInput
                        label="Checkout padrão"
                        name="hospedagem_checkout_padrao"
                        type="time"
                        value={regrasOperacionais.hospedagem.checkout_padrao}
                        onChange={(v) => atualizarRegraOperacional("hospedagem", "checkout_padrao", v)}
                      />
                      <FormInput
                        label="Tolerância (min)"
                        name="hospedagem_tolerancia_checkout_minutos"
                        type="number"
                        value={regrasOperacionais.hospedagem.tolerancia_checkout_minutos}
                        onChange={(v) => atualizarRegraOperacional("hospedagem", "tolerancia_checkout_minutos", v)}
                      />
                      <FormInput
                        label="Limite de atraso checkout"
                        name="hospedagem_late_checkout_limite"
                        type="time"
                        value={regrasOperacionais.hospedagem.late_checkout_limite}
                        onChange={(v) => atualizarRegraOperacional("hospedagem", "late_checkout_limite", v)}
                      />
                      <div>
                        <label className="mb-1 block text-sm font-semibold text-foreground">
                          Regra após o checkout
                        </label>
                        <select
                          value={regrasOperacionais.hospedagem.late_checkout_tipo}
                          onChange={(e) => atualizarRegraOperacional("hospedagem", "late_checkout_tipo", e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20"
                        >
                          <option value="creche_ate_limite_diaria_apos">Creche até o limite e diária após</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="hospedagem_taxa_atraso" className="mb-1 block text-sm font-semibold text-foreground">
                          Taxa extra opcional
                        </label>
                        <input
                          type="text"
                          id="hospedagem_taxa_atraso"
                          name="hospedagem_taxa_atraso"
                          inputMode="numeric"
                          value={formatarValorMonetario(regrasOperacionais.hospedagem.taxa_atraso)}
                          onChange={(e) => atualizarRegraOperacional("hospedagem", "taxa_atraso", normalizarValorMonetario(e.target.value))}
                          placeholder="R$ 0,00"
                          className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20 transition-smooth"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {servicos.includes(2) && (
                  <div className="rounded-2xl border border-border bg-card p-4">
                    <div className="mb-4">
                      <h3 className="text-base font-bold text-foreground">Creche</h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Defina a janela operacional da creche e como o atraso na retirada será tratado.
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <div className="sm:col-span-2 lg:col-span-3">
                        <label className="mb-2 block text-sm font-semibold text-foreground">
                          Cobrar atraso no checkout?
                        </label>
                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                            <input
                              type="radio"
                              name="creche_cobrar_atraso_checkout"
                              checked={regrasOperacionais.creche.cobrar_atraso_checkout === "1"}
                              onChange={() => atualizarRegraOperacional("creche", "cobrar_atraso_checkout", "1")}
                              className="h-4 w-4 accent-primary"
                            />
                            Sim
                          </label>
                          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                            <input
                              type="radio"
                              name="creche_cobrar_atraso_checkout"
                              checked={regrasOperacionais.creche.cobrar_atraso_checkout === "0"}
                              onChange={() => atualizarRegraOperacional("creche", "cobrar_atraso_checkout", "0")}
                              className="h-4 w-4 accent-primary"
                            />
                            Não
                          </label>
                        </div>
                      </div>

                      <FormInput
                        label="Check-in padrão"
                        name="creche_checkin_padrao"
                        type="time"
                        value={regrasOperacionais.creche.checkin_padrao}
                        onChange={(v) => atualizarRegraOperacional("creche", "checkin_padrao", v)}
                      />
                      <FormInput
                        label="Checkout padrão"
                        name="creche_checkout_padrao"
                        type="time"
                        value={regrasOperacionais.creche.checkout_padrao}
                        onChange={(v) => atualizarRegraOperacional("creche", "checkout_padrao", v)}
                      />
                      <FormInput
                        label="Tolerância (min)"
                        name="creche_tolerancia_checkout_minutos"
                        type="number"
                        value={regrasOperacionais.creche.tolerancia_checkout_minutos}
                        onChange={(v) => atualizarRegraOperacional("creche", "tolerancia_checkout_minutos", v)}
                      />
                      <FormInput
                        label="Limite do atraso grande"
                        name="creche_late_checkout_limite"
                        type="time"
                        value={regrasOperacionais.creche.late_checkout_limite}
                        onChange={(v) => atualizarRegraOperacional("creche", "late_checkout_limite", v)}
                      />
                      <div>
                        <label className="mb-1 block text-sm font-semibold text-foreground">
                          Regra após o atraso
                        </label>
                        <select
                          value={regrasOperacionais.creche.late_checkout_tipo}
                          onChange={(e) => atualizarRegraOperacional("creche", "late_checkout_tipo", e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20"
                        >
                          <option value="nova_creche">Cobrar nova creche após o limite</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="creche_taxa_atraso" className="mb-1 block text-sm font-semibold text-foreground">
                          Taxa de atraso
                        </label>
                        <input
                          type="text"
                          id="creche_taxa_atraso"
                          name="creche_taxa_atraso"
                          inputMode="numeric"
                          value={formatarValorMonetario(regrasOperacionais.creche.taxa_atraso)}
                          onChange={(e) => atualizarRegraOperacional("creche", "taxa_atraso", normalizarValorMonetario(e.target.value))}
                          placeholder="R$ 0,00"
                          className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20 transition-smooth"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </FormSection>
          )}

          {/* 4. Apresentação & Fotos */}
          <FormSection number={4} title="Apresentação & Fotos">
            <div>
              <label className="mb-1 block text-sm font-semibold text-foreground">
                {tipo === "pj" ? "Fale sobre sua empresa:" : tipo === "mv" ? "Fale sobre sua atuação como Médico Veterinário:" : "Escreva um pouco sobre você e sua relação com os pets:"}
              </label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20 transition-smooth resize-none"
                placeholder="Conte sua história com os pets..."
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Foto de Perfil */}
              <div>
                <h4 className="mb-2 text-sm font-bold text-foreground">
                  <Camera className="mr-1 inline h-4 w-4" />
                  {tipo === "pj" ? "Logo ou Foto da Empresa" : "Foto de Perfil"}
                </h4>
                <div className="relative flex h-32 items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-border bg-muted/50 transition-smooth hover:border-primary/50">
                  
                  {fotosAtuais.perfil ? (
                    <img src={fotosAtuais.perfil} alt="Perfil" className="h-full w-full object-cover pointer-events-none" />
                  ) : (
                    <div className="text-center pointer-events-none">
                      <Camera className="mx-auto mb-1 h-8 w-8 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Clique para enviar</span>
                    </div>
                  )}

                  <label className={`absolute inset-0 z-10 cursor-pointer flex flex-col items-center justify-center transition-opacity ${fotosAtuais.perfil ? 'bg-black/50 opacity-0 hover:opacity-100' : ''}`}>
                    {fotosAtuais.perfil && <span className="text-xs font-bold text-white pointer-events-none">Alterar foto</span>}
                    
                    {/* Aqui está o input correto e único! */}
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="sr-only" 
                      onChange={(e) => handleSelecionarFoto(e, -1)} 
                    />
                  </label>
                </div>
              </div>

              {/* Fotos do Espaço */}
              <div>
                <h4 className="mb-2 text-sm font-bold text-foreground">
                  {tipo === "pj" ? "Fotos do Local / Instalações" : tipo === "mv" ? "Fotos do atendimento" : "Fotos do Espaço"}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="relative flex h-20 items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-border bg-muted/50 transition-smooth hover:border-primary/50">
                      
                      {fotosAtuais.espaco[i] ? (
                        <img src={fotosAtuais.espaco[i]} alt={`Espaço ${i + 1}`} className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-xs text-muted-foreground">Foto {i + 1}</span>
                      )}

                      <label className={`absolute inset-0 cursor-pointer flex flex-col items-center justify-center transition-opacity ${fotosAtuais.espaco[i] ? 'bg-black/50 opacity-0 hover:opacity-100' : ''}`}>
                        {/* Agora o input está no lugar certo, e o React sabe quem é o "i" */}
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="sr-only" 
                          onChange={(e) => handleSelecionarFoto(e, i)} 
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Preferências */}
            <div className="mt-2 space-y-4">
              <h4 className="text-sm font-bold text-foreground">Preferências e Habilidades</h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-border p-3">
                  <p className="mb-2 text-xs font-bold text-foreground">🐾 Aceito:</p>
                  {(["aceita_caes", "aceita_gatos", "aceita_outros"] as const).map((k) => (
                    <label key={k} className="flex items-center gap-2 py-1 text-sm text-foreground cursor-pointer">
                      <input type="checkbox" checked={preferencias[k]} onChange={() => togglePref(k)} className="h-4 w-4 rounded border-input text-primary accent-primary" />
                      {k === "aceita_caes" ? "Cães" : k === "aceita_gatos" ? "Gatos" : "Outros Animais"}
                    </label>
                  ))}
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="mb-2 text-xs font-bold text-foreground">⚖️ Porte do Pet:</p>
                  {(["aceita_ate_10kg", "aceita_ate_25kg", "aceita_ate_40kg", "aceita_acima_40kg"] as const).map((k) => (
                    <label key={k} className="flex items-center gap-2 py-1 text-sm text-foreground cursor-pointer">
                      <input type="checkbox" checked={preferencias[k]} onChange={() => togglePref(k)} className="h-4 w-4 rounded border-input text-primary accent-primary" />
                      {k === "aceita_ate_10kg" ? "Até 10kg" : k === "aceita_ate_25kg" ? "Até 25kg" : k === "aceita_ate_40kg" ? "Até 40kg" : "Acima de 40kg"}
                    </label>
                  ))}
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="mb-2 text-xs font-bold text-foreground">🩺 Cuidados Especiais:</p>
                  {(["aceita_idosos", "aceita_filhotes", "administra_medicacao"] as const).map((k) => (
                    <label key={k} className="flex items-center gap-2 py-1 text-sm text-foreground cursor-pointer">
                      <input type="checkbox" checked={preferencias[k]} onChange={() => togglePref(k)} className="h-4 w-4 rounded border-input text-primary accent-primary" />
                      {k === "aceita_idosos" ? "Pets Idosos" : k === "aceita_filhotes" ? "Filhotes" : "Administra Medicação"}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </FormSection>

          {/* 6. MV Block */}
          {tipo === "mv" && (
            <FormSection number={6} title="Cadastro do Médico Veterinário">
              <div className="grid gap-3 sm:grid-cols-3">
                <FormInput label="CRMV-UF" name="mv_crmv" value={mvData.crmv} onChange={(v) => setMvData((p) => ({ ...p, crmv: v }))} required placeholder="Ex: CRMV-SP 12345" />
                <FormInput label="Telefone" name="mv_telefone" type="tel" value={mvData.telefone} onChange={(v) => setMvData((p) => ({ ...p, telefone: v }))} required />
                <FormInput label="E-mail profissional" name="mv_email" type="email" value={mvData.email} onChange={(v) => setMvData((p) => ({ ...p, email: v }))} required />
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-foreground">Modalidades de atendimento</p>
                <div className="flex flex-wrap gap-3">
                  {(["domiciliar", "teleconsulta", "consultorio"] as const).map((m) => (
                    <label key={m} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                      <input type="checkbox" checked={mvModalidades[m]} onChange={() => setMvModalidades((p) => ({ ...p, [m]: !p[m] }))} className="h-4 w-4 rounded border-input accent-primary" />
                      {m.charAt(0).toUpperCase() + m.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-foreground">Especialidade(s) / Áreas de atuação</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {especialidadesMV.map((esp) => (
                    <label key={esp} className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-xs text-foreground cursor-pointer transition-smooth hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={mvEspecialidades.includes(esp)}
                        onChange={() => setMvEspecialidades((p) => p.includes(esp) ? p.filter((e) => e !== esp) : [...p, esp])}
                        className="h-3.5 w-3.5 rounded border-input accent-primary"
                      />
                      {esp}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-foreground">Boas práticas e conformidade</p>
                {[
                  "Estou ativo(a) junto ao CRMV",
                  "Transporte e armazenamento de medicamentos conforme exigência legal",
                  "Descarte adequado de resíduos biológicos e farmacêuticos",
                  "Manutenção de prontuários dos atendimentos",
                  "Compromisso de seguir o Código de Ética do Médico-Veterinário",
                ].map((item, i) => (
                  <label key={i} className="flex items-center gap-2 py-1 text-sm text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={mvConformidades.includes(item)}
                      onChange={() => toggleConformidade(item)}
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    {item}
                  </label>
                ))}
              </div>
            </FormSection>
          )}

          {tipo === "mv" && (
            <FormSection number={7} title="Documentos do Médico Veterinário">
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-foreground">
                    Documento oficial com foto
                  </label>
                  {mvDocumentosAtuais.doc_foto && (
                    <p className="mb-2 text-xs text-muted-foreground">
                      Arquivo atual: <a href={mvDocumentosAtuais.doc_foto} target="_blank" rel="noreferrer" className="font-bold text-primary hover:underline">ver</a>
                    </p>
                  )}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) =>
                      setMvArquivos((prev) => ({ ...prev, doc_foto: e.target.files?.[0] || null }))
                    }
                    className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-semibold text-foreground">
                    Comprovante de CRMV ativo
                  </label>
                  {mvDocumentosAtuais.doc_crmv && (
                    <p className="mb-2 text-xs text-muted-foreground">
                      Arquivo atual: <a href={mvDocumentosAtuais.doc_crmv} target="_blank" rel="noreferrer" className="font-bold text-primary hover:underline">ver</a>
                    </p>
                  )}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) =>
                      setMvArquivos((prev) => ({ ...prev, doc_crmv: e.target.files?.[0] || null }))
                    }
                    className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-semibold text-foreground">
                    Comprovante de endereço
                  </label>
                  {mvDocumentosAtuais.doc_endereco_prof && (
                    <p className="mb-2 text-xs text-muted-foreground">
                      Arquivo atual: <a href={mvDocumentosAtuais.doc_endereco_prof} target="_blank" rel="noreferrer" className="font-bold text-primary hover:underline">ver</a>
                    </p>
                  )}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) =>
                      setMvArquivos((prev) => ({ ...prev, doc_endereco_prof: e.target.files?.[0] || null }))
                    }
                    className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </FormSection>
          )}

          {/* 7. Documentos PF/PJ */}
          {(tipo === "pf" || tipo === "pj") && (
            <FormSection number={7} title="Documentos de Identificação">
              <div className="grid gap-4 sm:grid-cols-2">
                <FileUpload
                  label="Documento de Identidade (frente)"
                  name="documento_frente"
                  hint="Pode ser RG ou CNH."
                  currentFile={documentosAtuais.documento_frente}
                />

                <FileUpload
                  label="Documento de Identidade (verso)"
                  name="documento_verso"
                  currentFile={documentosAtuais.documento_verso}
                />

                <FileUpload
                  label="Selfie segurando seu documento"
                  name="selfie_documento"
                  hint="A foto deve estar nítida, mostrando seu rosto e o documento."
                  currentFile={documentosAtuais.selfie_documento}
                />

                <FileUpload
                  label="Comprovante de endereço"
                  name="comprovante_endereco"
                  hint="Conta de luz, água, etc. (últimos 3 meses)."
                  currentFile={documentosAtuais.comprovante_endereco}
                />
              </div>
            </FormSection>
          )}

          {/* 8. Endereço */}
          <FormSection number={8} title="Endereço de Atendimento">
            <button
              type="button"
              className="mb-3 rounded-lg border border-primary px-4 py-2 text-sm font-semibold text-primary transition-smooth hover:bg-forest-light"
              onClick={() => toast.info("Endereço copiado do perfil!")}
            >
              <MapPin className="mr-1 inline h-4 w-4" />
              Usar o mesmo endereço do meu perfil
            </button>
            <div className="grid gap-3 sm:grid-cols-3">
              <FormInput label="CEP" name="cep" value={endereco.cep} onChange={(v) => setEndereco((p) => ({ ...p, cep: v }))} required />
              <FormInput label="Endereço" name="endereco" value={endereco.endereco} onChange={(v) => setEndereco((p) => ({ ...p, endereco: v }))} required />
              <FormInput label="Número" name="numero" value={endereco.numero} onChange={(v) => setEndereco((p) => ({ ...p, numero: v }))} required />
              <FormInput label="Bairro" name="bairro" value={endereco.bairro} onChange={(v) => setEndereco((p) => ({ ...p, bairro: v }))} required />
              <FormInput label="Cidade" name="cidade" value={endereco.cidade} onChange={(v) => setEndereco((p) => ({ ...p, cidade: v }))} required />
              <FormInput label="Estado" name="estado" value={endereco.estado} onChange={(v) => setEndereco((p) => ({ ...p, estado: v }))} required />
            </div>
            <FormInput label="Complemento" name="complemento" value={endereco.complemento} onChange={(v) => setEndereco((p) => ({ ...p, complemento: v }))} />
          </FormSection>

          {/* 9. Dados Bancários */}
          <FormSection number={9} title="Dados Bancários para Recebimento">
            <FormInput
              label="Chave PIX"
              name="chave_pix"
              value={chavePix}
              onChange={setChavePix}
              required
              hint="Informe a chave PIX (CPF, CNPJ, e-mail, celular ou aleatória) para receber os pagamentos."
              placeholder="Ex: seu@email.com"
            />
          </FormSection>

          {/* 10. Política de Cancelamento */}
          <FormSection number={10} title="Política de Cancelamento">
            <div className="grid gap-3 sm:grid-cols-3">
              <RadioOption name="politica" value="livre" label="🟢 Livre" description="100% reembolso antes do início" checked={politica === "livre"} onChange={setPolitica} />
              <RadioOption name="politica" value="moderada" label="🟡 Intermediária" description="100% até 3 dias antes" checked={politica === "moderada"} onChange={setPolitica} />
              <RadioOption name="politica" value="fixa" label="🔴 Fixa" description="100% até 7 dias antes" checked={politica === "fixa"} onChange={setPolitica} />
            </div>
            <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground space-y-1">
              <p><strong>🟢 Livre:</strong> 100% de reembolso antes do início. Depois: 50%.</p>
              <p><strong>🟡 Intermediária:</strong> 100% até 3 dias antes. Menos de 3 dias: 50%.</p>
              <p><strong>🔴 Fixa:</strong> 100% até 7 dias antes. Menos de 7 dias: 50%.</p>
            </div>
          </FormSection>

          {/* 11. Confirmações */}
          <FormSection number={11} title="Confirmações Finais">
            <div className="space-y-2">
              {[
                { key: "faq", label: "Li e estou de acordo com os Termos de Adesão Anfitrião" },
                { key: "video", label: "Li e estou de acordo com o Manual do Anfitrião" },
                { key: "termos", label: "Declaro que não possuo antecedentes criminais e estou ciente das responsabilidades legais." },
              ].map((t) => (
                <label key={t.key} className="flex items-start gap-2 text-sm text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termosAceitos[t.key as keyof typeof termosAceitos]}
                    onChange={() => setTermosAceitos((p) => ({ ...p, [t.key]: !p[t.key as keyof typeof termosAceitos] }))}
                    className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                    required
                  />
                  {t.label}
                </label>
              ))}
            </div>
          </FormSection>

          {/* Submit */}
          <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
            <button
              type="submit"
              className="w-full rounded-xl gradient-primary py-4 text-lg font-bold text-primary-foreground shadow-lg transition-smooth hover:shadow-xl"
            >
              <Save className="mr-2 inline h-5 w-5" />
              Salvar Perfil
            </button>
          </motion.div>
        </form>
      </main>

      {/* MODAL DE CORTE FICA AQUI (Fora do form, mas dentro da div principal) */}
      <ModalCorteImagem
        isOpen={modalCorteProps.isOpen}
        imagemSrc={modalCorteProps.imagemSrc}
        aspectRatio={modalCorteProps.targetIndex === -1 ? 1 : 16 / 9}
        onClose={() => setModalCorteProps({ ...modalCorteProps, isOpen: false })}
        onCropComplete={(imagemCortadaUrl) => {
          if (modalCorteProps.targetIndex === -1) {
            setFotosAtuais(prev => ({ ...prev, perfil: imagemCortadaUrl }));
          } else {
            const novasFotosEspaco = [...fotosAtuais.espaco];
            novasFotosEspaco[modalCorteProps.targetIndex] = imagemCortadaUrl;
            setFotosAtuais(prev => ({ ...prev, espaco: novasFotosEspaco }));
          }
        }}
      />
    </div>
  );
};

export default EditarPerfil;
